import { useEffect, useState } from 'react';
import type { SessionState } from '@mlc/shared';
import { readSession } from './api';

/**
 * Qui est connecté (US-49).
 *
 * Plusieurs écrans en ont besoin — la fiche pour savoir si la recette est la
 * sienne, la bibliothèque pour le tri en lot, les réglages pour le rôle — et la
 * réponse change rarement. Elle est donc lue **une fois** et mémorisée dans le
 * module : trois écrans qui interrogeraient chacun `/api/session` au montage
 * feraient trois allers-retours pour la même information.
 *
 * **Le cache s'invalide depuis US-52.** Il ne l'était pas, au motif que « la
 * seule façon d'en changer est de se reconnecter, ce qui recharge la page ».
 * Ce n'est plus vrai : l'écran de profil laisse modifier son prénom, son nom et
 * son adresse sans quitter la page, et un nom qui ne changerait qu'au prochain
 * rechargement passerait pour un enregistrement raté.
 */

let cache: SessionState | null = null;
let enCours: Promise<SessionState> | null = null;

/** Les composants montés, prévenus quand l'état change sous leurs pieds. */
const abonnes = new Set<(etat: SessionState | null) => void>();

function publier(): void {
  for (const abonne of abonnes) abonne(cache);
}

export function chargerSession(): Promise<SessionState> {
  if (cache !== null) return Promise.resolve(cache);
  enCours ??= readSession()
    .then((etat) => { cache = etat; publier(); return etat; })
    .finally(() => { enCours = null; });
  return enCours;
}

/** Vide le cache — utile aux tests et après une déconnexion. */
export function oublierSession(): void {
  cache = null;
  enCours = null;
  publier();
}

/**
 * Relit l'état et prévient tous les écrans montés (US-52).
 *
 * Appelée après une modification de son propre profil : c'est ce qui fait que
 * « Connecté en tant que Claire » devient « Connecté en tant que Claire M. »
 * sans rechargement.
 */
export function rafraichirSession(): Promise<SessionState> {
  cache = null;
  enCours = null;
  return chargerSession();
}

export function useSession(): SessionState | null {
  const [etat, setEtat] = useState<SessionState | null>(cache);

  useEffect(() => {
    let vivant = true;
    const abonne = (valeur: SessionState | null): void => { if (vivant) setEtat(valeur); };
    abonnes.add(abonne);
    chargerSession()
      .then((valeur) => { if (vivant) setEtat(valeur); })
      .catch(() => { /* hors ligne : les écrans se contentent de ne rien affirmer */ });
    return () => { vivant = false; abonnes.delete(abonne); };
  }, []);

  return etat;
}
